import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { Roles } from './authentication/roles';
import { ProfileComponent } from './profile/profile.component';
import { SignupInvitationLandingComponent } from './signup/signup-invitation-landing/signup-invitation-landing.component';
import { SetupPlatformOverviewComponent } from './setup/setup-platform-overview/setup-platform-overview.component';
import { LoginComponent } from './authentication/login/login.component';
import { AuthCallbackComponent } from './authentication/auth-callback/auth-callback.component';
import { ValidateEmailComponent } from './employee/verify-email/validate-email.component';
import { authenticationGuard } from './authentication/authentication.guard';
import { DeviceSetupComponent } from './device-setup/device-setup.component';
import { InquiryDeeplinkComponent } from './deeplink/inquiry-deeplink/inquiry-deeplink.component';
import { AttachmentDeeplinkComponent } from './deeplink/attachment-deeplink/attachment-deeplink.component';
import { EmbeddedCompletedComponent } from './embedded/embedded-completed.component';
import { LoggedOutComponent } from './authentication/logged-out/logged-out.component';
import { SalesforceLaunchComponent } from './integrations/salesforce/salesforce-launch.component';
import { SalesforceNotAuthorizedComponent } from './integrations/salesforce/salesforce-not-authorized.component';
import { environment } from '../environments/environment';
import { getDebugRoutes } from './debug/debug-route';
import { videoAppointmentRoute } from './app-route-paths';
import { VideoAppointmentLaunchComponent } from './integrations/video-appointment-launch/video-appointment-launch.component';
import { VideoAppointmentLaunchUnavailableComponent } from './integrations/video-appointment-launch/video-appointment-launch-unavailable.component';
import { createWorkflowOperationAppRoutes } from './workflows/workflow-routing.config';

export const routes: Routes = [
  { path: '', component: LoginComponent },
  { path: 'auth-callback', component: AuthCallbackComponent },
  { path: 'integration/salesforce', component: SalesforceLaunchComponent },
  {
    path: 'integration/video-appointment',
    pathMatch: 'full',
    redirectTo: 'integration/video-appointment/not-available',
  },
  {
    path: 'integration/video-appointment/not-available',
    component: VideoAppointmentLaunchUnavailableComponent,
  },
  {
    path: 'integration/video-appointment/:token',
    component: VideoAppointmentLaunchComponent,
  },
  {
    path: 'integration/salesforce/not-authorized',
    component: SalesforceNotAuthorizedComponent,
  },
  { path: 'completed', component: EmbeddedCompletedComponent },
  {
    path: 'validate-email',
    component: ValidateEmailComponent,
  },
  {
    path: 'video-appointments',
    loadChildren: () =>
      import('./scheduling/scheduling-routing.module').then(
        (m) => m.SchedulingRoutingModule,
      ),
    canActivate: [authenticationGuard],
  },
  {
    path: 'scheduling',
    pathMatch: 'full',
    redirectTo: 'video-appointments',
  },
  {
    path: 'scheduling',
    loadChildren: () =>
      import('./scheduling/scheduling-routing.module').then(
        (m) => m.SchedulingRoutingModule,
      ),
    canActivate: [authenticationGuard],
  },
  {
    path: 'attachment',
    loadChildren: () =>
      import('./attachment/attachment.module').then((m) => m.AttachmentModule),
    canActivate: [authenticationGuard],
  },
  {
    path: 'history',
    redirectTo: 'cases',
  },
  {
    path: 'cases',
    loadChildren: () =>
      import('./history/history.module').then((m) => m.HistoryModule),
    canActivate: [authenticationGuard],
  },
  {
    path: 'tasks',
    loadChildren: () =>
      import('./case-tasks/task-overview/task-overview.routes').then(
        (m) => m.taskOverviewRoutes,
      ),
    canActivate: [authenticationGuard],
  },
  ...createWorkflowOperationAppRoutes(authenticationGuard),
  {
    path: 'customer',
    loadChildren: () =>
      import('./customer/customer.module').then((m) => m.CustomerModule),
    canActivate: [authenticationGuard],
  },
  {
    path: 'settings',
    loadChildren: () =>
      import('./settings/settings.module').then((m) => m.SettingsModule),
    canActivate: [authenticationGuard],
  },
  {
    path: 'employee',
    loadChildren: () =>
      import('./employee/employee.module').then((m) => m.EmployeeModule),
    canActivate: [authenticationGuard],
    data: { roles: [Roles.Manager] },
  },
  {
    path: 'profile',
    component: ProfileComponent,
    canActivate: [authenticationGuard],
  },
  {
    path: 'notifications/deliveries/:id',
    loadComponent: () =>
      import('./notification/message-delivery-details/message-delivery-details.component').then(
        (module) => module.MessageDeliveryDetailsComponent,
      ),
    canActivate: [authenticationGuard],
  },
  {
    path: 'device-setup',
    component: DeviceSetupComponent,
    canActivate: [authenticationGuard],
  },
  ...getDebugRoutes(environment),
  {
    path: 'statistics',
    redirectTo: 'video-appointments/statistics',
    pathMatch: 'full',
  },
  {
    path: videoAppointmentRoute,
    loadChildren: () =>
      import('./video-appointment/video-appointment.module').then(
        (m) => m.VideoAppointmentModule,
      ),
    canActivate: [authenticationGuard],
  },
  {
    path: 'signup',
    component: SignupInvitationLandingComponent,
  },
  {
    path: 'setup',
    component: SetupPlatformOverviewComponent,
    canActivate: [authenticationGuard],
  },
  {
    path: 'logged-out',
    component: LoggedOutComponent,
  },
  {
    path: 'deeplink/inquiry/:id',
    component: InquiryDeeplinkComponent,
  },
  {
    path: 'deeplink/attachment/:id',
    component: AttachmentDeeplinkComponent,
  },
  {
    path: '**',
    title: $localize`:@@pageNotFoundTitle:Seite nicht gefunden`,
    loadComponent: () =>
      import('./shared/not-found/not-found.component').then(
        (module) => module.NotFoundComponent,
      ),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // Keep the current history entry when a leave guard cancels browser Back.
      canceledNavigationResolution: 'computed',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
